
import React, { useState } from 'react';
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from '@/hooks/use-toast';
import { HoneypotField } from '@/components/ui/honeypot-field';
import { contactFormSchema, ContactFormValues } from './ContactFormSchema';
import { sendContactForm } from '@/utils/contactFormService';
import { CheckCircle, AlertTriangle } from 'lucide-react';

// Import our components
import FormField from './FormField';
import SubmitButton from './SubmitButton';
import ContactFormSource from './ContactFormSource';
import PrivacyDisclaimer from './PrivacyDisclaimer';

const ContactForm = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [submitAttempts, setSubmitAttempts] = useState(0);
  
  // Initialize form with React Hook Form + Zod validation
  const form = useForm<ContactFormValues>({
    resolver: zodResolver(contactFormSchema),
    defaultValues: {
      name: "",
      company: "",
      email: "",
      phone: "",
      message: "",
      source: "",
      website: "", // Initialize honeypot field as empty
    },
  });

  const onSubmit = async (values: ContactFormValues) => {
    if (loading) {
      console.log('🚫 Form already submitting, ignoring duplicate attempt');
      return;
    }

    setLoading(true);
    setSubmitAttempts(prev => prev + 1);
    
    try {
      console.log('📝 Contact form submission attempt #', submitAttempts + 1);
      
      // Use the contactFormService to send the form data
      const result = await sendContactForm(
        values, 
        "Nieuw contactformulier ingediend",
        { 
          source: values.source || "Website contactpagina",
          'Submission attempt': submitAttempts + 1,
        },
        'bert@afsprakenmaker.be'
      );
      
      if (result.success) {
        console.log('✅ Contact form submitted successfully!');
        
        // Track the form submission as a conversion
        const { trackConversion } = await import('@/utils/analyticsService');
        trackConversion();
        
        toast({
          title: "Bericht verzonden!",
          description: "We nemen binnen 24 uur contact met je op voor een vrijblijvend gesprek.",
          action: <CheckCircle className="h-4 w-4 text-green-600" />,
        });
        form.reset();
        setSubmitAttempts(0);
      } else {
        console.error('❌ Contact form submission failed:', result.error);
        throw new Error(result.error || 'Email verzending mislukt');
      }
    } catch (error) {
      console.error("❌ Contact form submission error:", error);
      
      const errorMessage = error instanceof Error ? error.message : 'Onbekende fout';
      
      toast({
        title: "Fout bij verzenden",
        description: submitAttempts >= 2 
          ? "Problemen met het formulier? Neem direct contact op via bert@afsprakenmaker.be of bel +32 9 395 02 85"
          : `Fout: ${errorMessage}. Probeer het opnieuw.`,
        variant: "destructive",
        action: <AlertTriangle className="h-4 w-4 text-red-600" />,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white p-6 md:p-8 rounded-lg shadow-md border border-gray-200">
      <h2 className="text-2xl font-bold mb-6 text-gray-900">Neem contact op</h2>
      
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          {/* Honeypot field - invisible to real users */}
          <HoneypotField form={form} />
          
          <FormField
            form={form}
            name="name"
            label="Naam*"
          >
            <Input placeholder="Jouw naam" disabled={loading} />
          </FormField>
          
          <FormField
            form={form}
            name="company"
            label="Bedrijf*"
          >
            <Input placeholder="Jouw bedrijfsnaam" disabled={loading} />
          </FormField>
          
          <div className="grid md:grid-cols-2 gap-6">
            <FormField
              form={form}
              name="email"
              label="E-mail*"
            >
              <Input type="email" placeholder="jouw@email.be" disabled={loading} />
            </FormField>
            
            <FormField
              form={form}
              name="phone"
              label="Telefoonnummer*"
            >
              <Input type="tel" placeholder="+32 ..." disabled={loading} />
            </FormField>
          </div>
          
          <FormField
            form={form}
            name="message"
            label="Bericht"
          >
            <Textarea 
              placeholder="Vertel ons kort over je bedrijf en wat je zoekt..." 
              rows={4}
              className="resize-none"
              disabled={loading}
            />
          </FormField>
          
          <ContactFormSource form={form} />
          
          <SubmitButton loading={loading} />
          
          <PrivacyDisclaimer />

          {submitAttempts > 0 && !loading && (
            <div className="text-xs text-gray-500 text-center">
              Problemen? Bel direct: +32 9 395 02 85 of email: bert@afsprakenmaker.be
            </div>
          )}
        </form>
      </Form>
    </div>
  );
};

export default ContactForm;
